import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import { Dropdown as SemanticDropdown } from 'semantic-ui-react';
import Checkbox from 'common/components/atoms/Checkbox';
import { scrollbar } from 'common/styles/scrollbar';
import RadioButton from '../molecules/Radiobutton';
import FlexContainer from './FlexContainer';

const Span = styled.span`
  margin-left: 10px;
`;

const SearchInputContainer = styled.div`
  position: sticky;
  top: 0;
  /* Above the Checkbox atom, whose Container is position:relative z-index:2. At an
     equal z-index the later DOM node wins, so the option checkboxes painted over
     this bar as the list scrolled under it. */
  z-index: 5;
  padding: 8px;
  background-color: ${props => props.theme.colors.white};
  border-bottom: 1px solid ${props => props.theme.colors.greyXL};
`;

const SearchInput = styled.input`
  width: 100%;
  border: 1px solid ${props => props.theme.colors.borderGray};
  border-radius: 4px;
  padding: 8px;
  font-size: 13px;
  font-family: "Petasense Open Sans";
  color: ${props => props.theme.colors.greyXXD};
`;

const DropdownCont = styled(SemanticDropdown)`
  display: flex !important;
  ${props => props.fontSettings ? props.fontSettings : props.theme.fonts.lightTitle}
  &.ui.fluid.dropdown {
    width: auto;
    flex: 1;
  }
  &.ui.dropdown.error {
    color: ${props => props.theme.colors.error};
    outline-color: ${props => props.theme.colors.error};
    border-color: ${props => props.theme.colors.error}!important;
  }
  &.ui.disabled.dropdown {
    background-color: ${props => props.theme.colors.disabled};
    color: ${props => props.theme.colors.greyXD};
    opacity: 1;
    border-color: ${props => props.theme.colors.disabled};
  }
  &.ui.active.selection.dropdown {
    border-color: ${props => props.theme.primaryColor};
    box-shadow: 0 0 8px 0 rgba(60,61,58,0.24);
  }
  &.ui.selection.active.dropdown .menu {
    border-color: ${props => props.theme.primaryColor};
    box-shadow: 0px 6px 8px 0px rgba(60,61,58,0.24);
    ${scrollbar}
  }
  &.ui.selection.active.dropdown .menu>.item {
    ${props => props.optionFontSettings ? props.optionFontSettings : props.theme.fonts.lightTitle}
    color: ${props => props.theme.colors.greyXXD};
  }
  &.ui.search {
    ${props => props.fontSettings}
  }
  a {
    font-size: 14px !important;
    font-family: 'Petasense Open Sans' !important;
    text-align: center !important;
    line-height: 1.4 !important;
  }
  ${Checkbox} {
    font-size: 14px;
  }
  ${props => props.preventContentWrap && `
    &.ui.selection.dropdown .menu>.item {
      white-space: nowrap;
    }
    .menu.transition.visible {
      width: auto !important;
    }
  `}
`;
const GroupHeaderItem = styled(SemanticDropdown.Item)`
  &&& {
    font-weight: 700;
    cursor: default;
    opacity: 1;
  }
`;

const Dropdown = ({
  error,
  onClickSelectable,
  selectable,
  selectableValue,
  selectableProps,
  multiCheckbox,
  options,
  toggleSelection,
  isItemSelected,
  noResultsMessage,
  showSearchInMenu,
  searchPlaceholder,
  ...rest
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  // "selection" is pulled out deliberately: callers such as InputField default it to
  // true, and Semantic UI rejects it alongside children. The multiCheckbox branch
  // renders its own menu as children, so it re-applies the class instead.
  const { onClose, className, selection, ...dropdownProps } = rest;

  const resolvedNoResultsMessage = noResultsMessage || i18n.t('common.noResultsFound', {
    defaultValue: 'No results found',
  });

  const filteredOptions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!showSearchInMenu || !query) {
      return options;
    }

    const hasGroups = options.some(option => option.isGroupHeader);
    if (!hasGroups) {
      return options.filter(option => String(option.text || '').toLowerCase().includes(query));
    }

    const filtered = [];
    let activeGroupHeader = null;
    let activeGroupItems = [];

    const flushGroup = () => {
      if (activeGroupHeader && activeGroupItems.length) {
        filtered.push(activeGroupHeader, ...activeGroupItems);
      }
      activeGroupHeader = null;
      activeGroupItems = [];
    };

    options.forEach((option) => {
      if (option.isGroupHeader) {
        flushGroup();
        activeGroupHeader = option;
        return;
      }
      if (!String(option.text || '').toLowerCase().includes(query)) {
        return;
      }
      if (activeGroupHeader) {
        activeGroupItems.push(option);
      } else {
        filtered.push(option);
      }
    });

    flushGroup();
    return filtered;
  }, [options, searchQuery, showSearchInMenu]);

  // Semantic UI forbids passing the "selection" prop together with children, and doing
  // so leaves the menu unpositioned. The multiCheckbox branch renders its own menu as
  // children, so it applies the same "selection" class through className instead.
  const selectionClassName = ['selection', className].filter(Boolean).join(' ');

  return (
    <>
      {selectable === 'radio' && (
        <RadioButton {...selectableProps} inputField onClick={onClickSelectable} value={selectableValue} />
      )}
      {multiCheckbox ? (
        <DropdownCont
          fluid
          className={selectionClassName}
          error={error}
          noResultsMessage={resolvedNoResultsMessage}
          onClose={(...args) => {
            setSearchQuery('');
            if (onClose) {
              onClose(...args);
            }
          }}
          {...dropdownProps}
        >
          <SemanticDropdown.Menu>
            {showSearchInMenu && (
              <SearchInputContainer onClick={e => e.stopPropagation()}>
                <SearchInput
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={e => e.stopPropagation()}
                  placeholder={searchPlaceholder}
                />
              </SearchInputContainer>
            )}
            {filteredOptions.map(({
              value,
              text,
              isGroupHeader,
              disabled
            }) => {
              if (isGroupHeader) {
                return (
                  <GroupHeaderItem
                    key={value}
                    disabled
                    onClick={e => e.stopPropagation()}
                  >
                    {text}
                  </GroupHeaderItem>
                );
              }
              const checked = isItemSelected(value);
              return (
                <SemanticDropdown.Item
                  key={value}
                  disabled={disabled}
                  onClick={(e) => {
                    // Semantic still fires onClick for a disabled item, and the
                    // event would otherwise bubble to the dropdown root and close
                    // the menu, so stop it before bailing out.
                    e.stopPropagation();
                    if (disabled) {
                      return;
                    }
                    toggleSelection(value, checked);
                  }}
                >
                  <FlexContainer alignItems="center" width="100%">
                    <Checkbox value={checked} />
                    <Span>{text}</Span>
                  </FlexContainer>
                </SemanticDropdown.Item>
              );
            })}
          </SemanticDropdown.Menu>
        </DropdownCont>
      ) : (
        <DropdownCont
          fluid
          selection={selection !== false}
          className={className}
          options={options}
          error={error}
          selectOnBlur={false}
          onClose={onClose}
          noResultsMessage={resolvedNoResultsMessage}
          {...dropdownProps}
        />
      )}
    </>
  );
};


/*
  "multiCheckbox": This is used to render multi-select dropdown, where dropdown items are rendered with checkboxes beside them
    Note: "multiCheckbox" is an alternative way to "multiple" prop provided by Semantic UI React dropdown.
    Refer to the documentation for "multiple" usage

  "isItemSelected": Helper function to decide whether item is checked, used with "multiCheckbox"
  "toggleSelection": Helper function to select/de-select an item, used with "multiCheckbox"

  "preventContentWrap": Sets width of dropdown items and selection button independently
    For example, if items in dropdown are ["a", "bigword"] and "a" is currently selected,
    then dropdown component itself can have width of "a",
    but the 'items menu' ( which is floating ) can expand to accomodate "bigword"
*/
Dropdown.propTypes = {
  multiCheckbox: PropTypes.bool,
  isItemSelected: PropTypes.func,
  toggleSelection: PropTypes.func,
  showSearchInMenu: PropTypes.bool,
  searchPlaceholder: PropTypes.string
};

Dropdown.defaultProps = {
  multiCheckbox: false,
  isItemSelected: () => {},
  toggleSelection: () => {},
  showSearchInMenu: false,
  searchPlaceholder: 'Search...'
};

Dropdown.displayName = 'Dropdown';

export default Dropdown;
